import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { T, CARD, Field, DarkInput, GoldBtn, SaveBtn } from './ContentShared';

const ProjectsEditor = ({ data, setData, onSave, saving }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  if (!data) return null;

  const updateProject = (idx, field, value) => {
    const updated = [...data.projects];
    updated[idx] = { ...updated[idx], [field]: value };
    setData({ ...data, projects: updated });
  };

  const addProject = () => {
    const p = { project_id: `project_${Date.now()}`, name_fr: '', name_en: '', short_name: '', tagline_fr: '', tagline_en: '', description_fr: '', description_en: '', image: '', color: '#059669', url: '', is_external: true };
    setData({ ...data, projects: [...data.projects, p] });
    setExpandedIdx(data.projects.length);
  };

  const removeProject = (idx) => {
    if (!window.confirm('Supprimer ?')) return;
    setData({ ...data, projects: data.projects.filter((_, i) => i !== idx) });
    setExpandedIdx(null);
  };

  return (
    <div className="space-y-6" data-testid="projects-editor">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: T.textSecondary, fontFamily: "'Montserrat', sans-serif" }}>{data.projects.length} projet(s)</p>
        <GoldBtn onClick={addProject} testId="add-project-btn"><Plus className="w-4 h-4" /> Ajouter</GoldBtn>
      </div>
      <div className="space-y-3">
        {data.projects.map((p, idx) => (
          <div key={p.project_id || `proj-${idx}`} className="rounded-2xl overflow-hidden" style={CARD}>
            <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} data-testid={`project-header-${idx}`}>
              {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{p.name_fr || p.short_name || `Projet ${idx + 1}`}</p>
                <p className="text-xs truncate" style={{ color: T.textMuted }}>{p.tagline_fr}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={e => { e.stopPropagation(); removeProject(idx); }} className="p-1.5 rounded hover:bg-gray-100"><Trash2 className="w-4 h-4" style={{ color: '#f87171' }} /></button>
                {expandedIdx === idx ? <ChevronUp className="w-4 h-4" style={{ color: T.textMuted }} /> : <ChevronDown className="w-4 h-4" style={{ color: T.textMuted }} />}
              </div>
            </div>
            {expandedIdx === idx && (
              <div className="p-5 space-y-4" style={{ borderTop: `1px solid ${T.border}` }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Nom (FR)"><DarkInput value={p.name_fr} onChange={e => updateProject(idx, 'name_fr', e.target.value)} testId={`project-name-fr-${idx}`} /></Field>
                  <Field label="Nom (EN)"><DarkInput value={p.name_en} onChange={e => updateProject(idx, 'name_en', e.target.value)} /></Field>
                </div>
                <Field label="Nom court"><DarkInput value={p.short_name} onChange={e => updateProject(idx, 'short_name', e.target.value)} /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Accroche (FR)"><DarkInput value={p.tagline_fr} onChange={e => updateProject(idx, 'tagline_fr', e.target.value)} /></Field>
                  <Field label="Accroche (EN)"><DarkInput value={p.tagline_en} onChange={e => updateProject(idx, 'tagline_en', e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Description (FR)"><DarkInput value={p.description_fr} onChange={e => updateProject(idx, 'description_fr', e.target.value)} rows={2} /></Field>
                  <Field label="Description (EN)"><DarkInput value={p.description_en} onChange={e => updateProject(idx, 'description_en', e.target.value)} rows={2} /></Field>
                </div>
                <Field label="URL Image">
                  <DarkInput value={p.image} onChange={e => updateProject(idx, 'image', e.target.value)} placeholder="https://..." />
                  {p.image && <img src={p.image} alt="" className="mt-2 h-24 w-full object-cover rounded-xl opacity-80" />}
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Couleur">
                    <div className="flex items-center gap-2">
                      <input type="color" value={p.color} onChange={e => updateProject(idx, 'color', e.target.value)} className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent" />
                      <DarkInput value={p.color} onChange={e => updateProject(idx, 'color', e.target.value)} />
                    </div>
                  </Field>
                  <Field label="URL"><DarkInput value={p.url} onChange={e => updateProject(idx, 'url', e.target.value)} placeholder="https://..." /></Field>
                  <Field label="Externe ?">
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={p.is_external} onChange={e => updateProject(idx, 'is_external', e.target.checked)} className="w-4 h-4 rounded" />
                      <span className="text-sm flex items-center gap-1" style={{ color: T.textSecondary }}>
                        {p.is_external ? <><ExternalLink className="w-3.5 h-3.5" /> Externe</> : <><LinkIcon className="w-3.5 h-3.5" /> Interne</>}
                      </span>
                    </label>
                  </Field>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <SaveBtn onClick={() => onSave('projects', data)} saving={saving} testId="save-projects-btn" />
    </div>
  );
};

export default ProjectsEditor;
